import {
  Box,
  Button,
  CircularProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { type Theme } from "@mui/material/styles";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import type {
  SalesSteerMode,
  SalesSteerPreview,
  SalesSteeringTarget,
} from "@xop/dashboard-shared";

import { useCan } from "../../../auth/permissions.js";
import { salesApi } from "../client.js";
import { describeAgentError } from "../describeAgentError.js";
import { SteerCampaignDiff } from "./SteerCampaignDiff.js";

/**
 * Single freeform "steering" surface — the operator's only knob on the agent.
 *
 * The operator picks WHICH agent the note aims at, then types it. What happens
 * next differs by target, and the difference is the point:
 *
 *   **Lead Generator** — the note becomes a STANDING directive: the agent
 *   honours it on every cycle (missions, strategy, lead hunting) until it is
 *   archived.
 *
 *   **Harvester** — the note EDITS the live harvest campaign (2026-08-07).
 *   It is resolved into a patch — sectors, regions, goal — and the resulting
 *   change is rendered by `<SteerCampaignDiff>` above the Send button, so the
 *   operator confirms what their sentence resolved to. Sending applies it.
 *
 * ## Why the harvester half works this way
 *
 * It used to be a standing directive too, re-read on every beat as a
 * CONSTRAINT over the target matrix. On 2026-08-06 the CEO typed
 *
 *     investments banks, private wire networks, trader voice networks
 *     in USA and Europe
 *
 * and only the PLACES landed: sector extraction began at a trigger word
 * ("hunt" / "only" / "target" / "focus on" / "sectors"), this note had none, so
 * all three sectors were silently discarded — while the region words quietly
 * narrowed the sweep. The note read as accepted. Worse, three mechanisms
 * (steer-as-filter, steer-as-campaign-builder, campaign-as-truth) each
 * overruled the others, so even a note that DID parse could have half of it
 * deleted without a word on screen.
 *
 * One campaign is the truth now, and this box is how you write it in English.
 * The preview costs no extra click — it renders above the button that was
 * already there — but it means a sentence can never quietly redirect a live
 * sweep.
 */
const TARGET_HELP: Record<SalesSteeringTarget, string> = {
  leads:
    'Shapes the Lead Generator — its missions, strategy & lead hunting. Examples: "Focus on EU airports this week", "King Khaled Intl just announced a tower revamp — investigate", "Ignore RFIs, only RFPs".',
  harvester:
    'Name the sectors, the places, or just what you\'re after — any phrasing works. Examples: "investment banks and trader voice networks in the US and Europe", "add airports in the UK", "prioritise organisations with a published switchboard number".',
  both:
    "Both at once: a standing directive for the Lead Generator, and a change to the Harvester's live campaign.",
};

const TARGET_PLACEHOLDER: Record<SalesSteeringTarget, string> = {
  leads: "Tell the Lead Generator what to focus on…",
  harvester:
    'Tell the Harvester what to hunt — e.g. "investment banks and trader voice networks in the US and Europe"…',
  both: "Tell both agents what to focus on…",
};

/** A harvester note is resolved through the AI gateway, so we do NOT fire on
 *  every keystroke — only once typing settles. Long enough that a normal
 *  sentence costs one call, short enough that the diff feels like part of
 *  typing rather than a separate step. */
const PREVIEW_DEBOUNCE_MS = 900;
/** Below this the note isn't worth resolving (matches the send gate). */
const MIN_BODY = 3;

function steersHarvester(target: SalesSteeringTarget): boolean {
  return target === "harvester" || target === "both";
}

/** Pre-fill the box from elsewhere in the dialog. Used by the "Apply to
 *  campaign" button on a harvester note that never reached one — every note
 *  posted before 2026-08-07 is in that state, because back then it was read as
 *  a matrix constraint instead. Re-interpreting those silently would be the
 *  same mistake in the other direction, so the operator re-sends it through
 *  the normal preview and sees what it does.
 *
 *  `at` is a nonce, not a timestamp: seeding the SAME text twice must still
 *  refill the box after the operator has edited it. */
export interface SteeringSeed {
  body: string;
  target: SalesSteeringTarget;
  at: number;
}

export function SteeringNoteInput(
  { seed }: { seed?: SteeringSeed | undefined } = {},
): JSX.Element | null {
  const canEdit = useCan("sales:edit");
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const [target, setTarget] = useState<SalesSteeringTarget>("leads");
  const [flash, setFlash] = useState<string | null>(null);
  const [preview, setPreview] = useState<SalesSteerPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  /** The operator's override of the resolved `mode`, kept apart from the
   *  preview so re-resolving on the next keystroke doesn't silently undo it. */
  const [modeOverride, setModeOverride] = useState<SalesSteerMode | null>(null);

  const trimmed = body.trim();
  const wantsPreview = steersHarvester(target) && trimmed.length >= MIN_BODY;
  /** What actually gets sent: the operator's pick if they made one, else what
   *  the note resolved to. Declared here (not below the early return) so the
   *  send closure reads the same value the diff panel is rendering. */
  const mode: SalesSteerMode = modeOverride ?? preview?.resolved.mode ?? "replace";

  // Seeding replaces whatever is in the box: the operator clicked a specific
  // note to re-send, so the box must show that note and nothing else.
  const seedAt = seed?.at;
  useEffect(() => {
    if (!seed) return;
    setBody(seed.body);
    setTarget(seed.target);
    setModeOverride(null);
    // Keyed on the nonce so re-seeding the SAME text still refills the box.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedAt]);

  // Resolve the note once typing settles. Cancelled on every edit, so an
  // in-flight resolution for stale text can never overwrite a newer one.
  useEffect(() => {
    if (!wantsPreview) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    let cancelled = false;
    setPreviewing(true);
    const timer = window.setTimeout(() => {
      salesApi
        .previewSteer(trimmed)
        .then((next) => {
          if (cancelled) return;
          setPreview(next);
          setPreviewError(null);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          setPreview(null);
          setPreviewError(describeAgentError(err, "Could not work out what this would change."));
        })
        .finally(() => {
          if (!cancelled) setPreviewing(false);
        });
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      setPreviewing(false);
      window.clearTimeout(timer);
    };
  }, [trimmed, wantsPreview]);

  const send = useMutation({
    mutationFn: async (text: string) => {
      if (!steersHarvester(target)) {
        const ack = await salesApi.postSteeringNote(text, target);
        // The note is committed the moment this resolves; the strategy rewrite
        // runs behind it (2026-08-27 — it used to happen inline, so the
        // operator waited out a full Sonnet call). The dialog does NOT close
        // itself on send — this flash renders inline (below) for 6s while the
        // operator is still looking at the form, so point them at where the
        // fold's actual progress lives rather than repeating "saved".
        return ack.fold
          ? "Saved — folding it into the strategy now. Watch Active directives."
          : ack.message;
      }
      if (!preview) throw new Error("Still working out what this would change — one moment.");
      const result = await salesApi.applySteer(text, { ...preview.resolved, mode }, target);
      const verb = result.created ? "created" : "updated";
      const repointed = result.missions_repointed
        ? ` ${result.missions_repointed} queued searches re-pointed.`
        : "";
      // A "both" steer also queues a strategy fold for the lead half — the
      // harvester-facing sentence above used to be the WHOLE flash, so the
      // operator was told about only half of what their note did. `fold_id`
      // is null either when there was nothing to fold (harvester-only, not
      // reachable here) or when the enqueue itself failed and was swallowed
      // — say that honestly rather than staying silent on it.
      const foldNote =
        target === "both"
          ? result.fold_id
            ? " The lead strategy is being reshaped too. Watch Active directives."
            : " The lead strategy fold couldn't be queued — the strategist will pick this note up on its next regular pass regardless."
          : "";
      return `Campaign ${verb}: ${result.campaign.label}. The harvester sweeps this now.${repointed}${foldNote}`;
    },
    onSuccess: (message) => {
      setBody("");
      setPreview(null);
      setModeOverride(null);
      setFlash(message || "Saved.");
      qc.invalidateQueries({ queryKey: ["sales", "today"] });
      qc.invalidateQueries({ queryKey: ["sales", "steering", "active"] });
      // The Strategy tab's regen banner is now downstream of this box too — a
      // lead note may have just queued a fold.
      qc.invalidateQueries({ queryKey: ["sales", "strategy-regen"] });
      // The Campaigns tab is now downstream of this box — refresh it, or the
      // operator switches tabs and sees the campaign they just changed.
      qc.invalidateQueries({ queryKey: ["sales", "campaigns"] });
      window.setTimeout(() => setFlash(null), 6_000);
    },
    onError: (err) => {
      setFlash(describeAgentError(err, "Could not send the note."));
    },
  });

  if (!canEdit) return null;

  const blockedByPreview =
    wantsPreview && (previewing || !preview || preview.applicable === false);
  const disabled =
    send.isPending || trimmed.length < MIN_BODY || trimmed.length > 2000 || blockedByPreview;

  return (
    <Box
      component="section"
      aria-label="Steer the agent"
      sx={{
        display: "grid",
        gap: 1,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: (t: Theme) => t.shadows[1],
        p: 2.5,
      }}
    >
      <Typography component="h2" sx={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
        Steer the agent
      </Typography>

      {/* Which agent does this steer apply to? Lead Generator (default) /
          Harvester / Both — same names as the mode toggle + two-agent strip. */}
      <ToggleButtonGroup
        size="small"
        exclusive
        value={target}
        onChange={(_, next: SalesSteeringTarget | null) => {
          if (next) setTarget(next);
        }}
        aria-label="which agent to steer"
        sx={{ alignSelf: "start" }}
      >
        <ToggleButton value="leads" aria-label="steer the lead generator">Lead Generator</ToggleButton>
        <ToggleButton value="harvester" aria-label="steer the harvester">Harvester</ToggleButton>
        <ToggleButton value="both" aria-label="steer both agents">Both</ToggleButton>
      </ToggleButtonGroup>

      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
        {steersHarvester(target)
          ? "Drop a note any time — it changes what the Harvester hunts, by editing its live campaign. "
          : "Drop a note any time — it becomes a standing directive the agent keeps following until you archive it. "}
        {TARGET_HELP[target]}
      </Typography>
      <Box
        component="form"
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          if (!disabled) send.mutate(trimmed);
        }}
        sx={{ display: "grid", gap: 1 }}
      >
        <TextField
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
            // A fresh sentence gets a fresh reading — an override kept across
            // an unrelated edit would apply a mode the operator picked for
            // different words.
            setModeOverride(null);
          }}
          multiline
          minRows={3}
          inputProps={{ maxLength: 2000 }}
          placeholder={TARGET_PLACEHOLDER[target]}
          fullWidth
          sx={{ "& .MuiInputBase-root": { resize: "vertical" } }}
        />

        {wantsPreview && previewing ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.disabled" }}>
            <CircularProgress size={12} />
            <Typography sx={{ fontSize: 12 }}>Working out what this changes…</Typography>
          </Box>
        ) : null}

        {wantsPreview && !previewing && preview ? (
          <SteerCampaignDiff preview={preview} mode={mode} onModeChange={setModeOverride} />
        ) : null}

        {wantsPreview && !previewing && previewError ? (
          <Typography role="alert" sx={{ fontSize: 12, color: "error.main" }}>
            {previewError} Nothing has changed — try again, or edit the campaign
            directly on the Campaigns tab.
          </Typography>
        ) : null}

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
          <Box component="span" sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
            {trimmed.length}/2000
          </Box>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="small"
            disableElevation
            disabled={disabled}
            sx={{ fontSize: 14, fontWeight: 500, px: 1.5, py: 0.75 }}
          >
            {send.isPending ? "Sending…" : "Send to agent"}
          </Button>
        </Box>
        {flash && (
          <Typography sx={{ fontSize: 12, color: "text.secondary" }} role="status">
            {flash}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
